import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import pipe from 'lodash/fp/pipe';
import curry from 'lodash/fp/curry';
import cond from 'lodash/cond';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { containerPaddingS } from 'common/styles/variables';

import { SuiContainer, SuiTooltipPopover, ORIGIN_LEFT, ORIGIN_RIGHT } from '@splashthat/sui';

import { NODE_TYPE_QUESTION_MULTIPLE } from 'common/entities/formNode/nodeTypes';
import { getFormNode, getFormNodeIncludeOther } from 'common/entities/formNode/selectors';
import { INPUT_TYPE_HIDDEN } from 'common/entities/formNode/inputTypes';
import { getFormNodeIsRequired } from 'common/entities/formLogicRule/selectors';
import { getFormErrorStyles } from 'common/entities/form/styleConfiguration/selectors';

import QuestionNode from './question/QuestionNode';
import ContentNode from './content/ContentNode';
import TicketSelectionNode from './ticket/TicketSelectionNode';

import { updateFormGroupValue, updateFormTouched } from '../../actions/form';
import { updateFormNodeCancelPreiousEffect, updateFormNodeEffect } from '../../epics/form';
import {
    getFormError,
    getFormGroupError,
    getFormGroupInitialValue,
    getFormGroupValue,
    getFormInitialValue,
    getFormNodeTouched,
    getFormNodeVisibility,
    getFormValue,
} from '../../selectors/form';
import { getFormRendererFormId, getIsRegistrationOpen, getIsUpdating, getNodeShouldBeDisabled } from '../../selectors';
import FormErrorMessageForNode from '../errors/FormErrorMessageForNode';

import CustomContainer from '../CustomContainer';

import './FormNode.styl';

const BASE_CLASS_NAME = 'FormNode';
export const DISABLED_PROTECTED_MESSAGE =
    'This field is disabled to protect your privacy. Please contact the event organizer to modify protected field responses.';

const ErrorContainer = styled.div`
    margin-top: ${containerPaddingS};
`;

const DisabledProtectedQuestion = (props) => (
    <SuiTooltipPopover
        content={DISABLED_PROTECTED_MESSAGE}
        disabled
        popoverProps={{
            anchorOrigin: ORIGIN_LEFT,
            targetOrigin: ORIGIN_RIGHT,
        }}
    >
        <SuiContainer variation="full-width" data-qa={`${BASE_CLASS_NAME}__Container--disabled`}>
            <QuestionNode {...props} required={false} value={null} checked={false} />
        </SuiContainer>
    </SuiTooltipPopover>
);

const components = {
    // @TODO: For further performance improvements split QuestionNode into multiple components
    //  that all get from the redux store only the information they need
    question: (props) => (props.isProtected && props.disabled ? DisabledProtectedQuestion : QuestionNode),
    [NODE_TYPE_QUESTION_MULTIPLE]: () => QuestionNode,
    content: () => ContentNode,
    ticketSelectionNode: () => TicketSelectionNode,
    default: () => <React.Fragment />,
};

const containers = {
    question: (props) =>
        cond([
            [() => props.visible && props.inputType !== INPUT_TYPE_HIDDEN, () => CustomContainer],
            [() => true, () => SuiContainer],
        ])(),
    [NODE_TYPE_QUESTION_MULTIPLE]: () => CustomContainer,
    content: (props) => (props.visible ? CustomContainer : SuiContainer),
    ticketSelectionNode: () => SuiContainer,
};

/**
 * "Switch" component, renders a group or a question depending on the node type.
 */
const FormNode = (props) => {
    const [internalValue, setInternalValue] = useState(props.value);

    useEffect(() => {
        props.value !== internalValue && setInternalValue(props.value);
    }, [props.value]);

    const Component = (components[props.type] || components.default)(props);
    const CustomSpacingContainer = containers[props.type](props);

    return (
        <CustomSpacingContainer data-qa={`${BASE_CLASS_NAME}__Container`}>
            <Component
                {...props}
                value={internalValue}
                onBlur={() => {
                    props.updateFormValueCancelPrevious(props.nodeId, internalValue);
                    props.updateFormTouched();
                }}
                setFieldValue={(id, value) => {
                    setInternalValue(value);
                    props.updateFormValue(id, value);
                }}
            />
            {props.error && (
                <ErrorContainer>
                    <FormErrorMessageForNode
                        key={`${props.nodeId}-${props.nodeId}`}
                        nodeId={props.nodeId}
                        errorType={props.error}
                    />
                </ErrorContainer>
            )}
        </CustomSpacingContainer>
    );
};

FormNode.propTypes = {
    nodeId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    inputType: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.any,
    initialValue: PropTypes.any,
    visible: PropTypes.bool,
    touched: PropTypes.bool,
    error: PropTypes.array,
    labelMarkup: PropTypes.string,
    groupId: PropTypes.string,
    updateFormTouched: PropTypes.func.isRequired,
    updateFormValue: PropTypes.func.isRequired,
    updateFormValueCancelPrevious: PropTypes.func.isRequired,
    updateFormGroupValue: PropTypes.func.isRequired,
    name: PropTypes.string,
    isRegistrationOpen: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    isProtected: PropTypes.bool.isRequired,
};

FormNode.defaultProps = {
    label: null,
    error: null,
    value: '',
    initialValue: '',
    touched: false,
    visible: true,
    labelMarkup: null,
    groupId: null,
    inputType: undefined,
    name: '',
};

const mapStateToProps = (state, props) => {
    const node = getFormNode(state, props.nodeId);
    const formId = node ? node.formId : undefined;

    const initialValue = props.groupId
        ? getFormGroupInitialValue(state, {
              id: props.nodeId,
              group: props.groupId,
          })
        : getFormInitialValue(state, { id: props.nodeId });

    return {
        name: node.name,
        value: props.groupId
            ? getFormGroupValue(state, {
                  id: props.nodeId,
                  group: props.groupId,
              })
            : getFormValue(state, { id: props.nodeId }),
        initialValue,
        error: props.groupId
            ? getFormGroupError(state, {
                  id: props.nodeId,
                  group: props.groupId,
              })
            : getFormError(state, { id: props.nodeId }),
        touched: getFormNodeTouched(state, { id: props.nodeId }),
        visible: getFormNodeVisibility(state, {
            id: props.nodeId,
            groupId: props.groupId,
        }),
        required: getFormNodeIsRequired(state, formId, props.nodeId),
        disabled: getNodeShouldBeDisabled(state, node, initialValue),
        includeOther: getFormNodeIncludeOther(state, props.nodeId),
        errorColor: pipe(
            getFormRendererFormId,
            curry(getFormErrorStyles)(state),
            ({ color }) => color && color.value,
        )(state),
        type: node.type,
        inputType: node.inputType,
        label: node.label,
        nodeId: props.nodeId,
        labelMarkup: node.labelMarkup,
        helperText: node.helperText,
        placeholder: node.placeholder,
        availabilityStart: node.availabilityStart,
        availabilityEnd: node.availabilityEnd,
        // @TODO: For further performance improvements split the config into its values
        config: node.config,
        code: node.code,
        description: node.description,
        isRegistrationOpen: getIsRegistrationOpen(state),
        isUpdating: getIsUpdating(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    updateFormValue: (id, value) =>
        dispatch(
            updateFormNodeEffect({
                id,
                value,
                group: ownProps.groupId,
            }),
        ),
    updateFormValueCancelPrevious: (id, value) =>
        dispatch(
            updateFormNodeCancelPreiousEffect({
                id,
                value,
                group: ownProps.groupId,
            }),
        ),
    updateFormTouched: () => dispatch(updateFormTouched(ownProps.nodeId, true)),
    updateFormGroupValue,
});

export default connect(mapStateToProps, mapDispatchToProps)(FormNode);
